import { useMutation, useQuery } from "src/Hooks/useReactQuery";
import React, { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { Spinner } from "reactstrap";
import { useSetRecoilState } from "recoil";
import { projectResourcesState, projectListState, projectRoleState } from "src/recoil/atoms/global";
import { authState } from "src/recoil/atoms/auth";
import { getMe, getAccessToken, getProjectDetail, getProjectRole, getProjectList } from "src/services/auth";
import { ACCESS_TOKEN, removeLocalStorage } from "src/utils/storage";
import { REACT_APP_PORTAL_URL, REACT_APP_SITE_URL } from "src/constant/environment";
import APP_ROUTES from "src/routes/routes";
import { ROUTER_BY_RESOURCE, LOADING_STATUS, ROLES, STATUS_ERROR_CODE } from "src/constant/common";
import { usePatchedNavigate } from "src/Hooks/usePatchedNavigate";

const routeGuard =
  (WrappedComponent, resourceType, requiredRole = null) =>
  (wrappedProps) => {
    const setAuth = useSetRecoilState(authState);
    const setProjectResourcesState = useSetRecoilState(projectResourcesState);
    const setProjectListState = useSetRecoilState(projectListState);
    const setProjectRoleState = useSetRecoilState(projectRoleState);
    const [authenticated, setAuthenticated] = useState(LOADING_STATUS.LOADING);
    const [checkingResource, setCheckingResource] = useState(true);
    const [checkingRole, setCheckingRole] = useState(requiredRole ? true : false);
    const location = useLocation();
    const navigate = usePatchedNavigate();
    const queryParams = new URLSearchParams(location.search);
    const sns_token = queryParams.get("sns_token");
    const projectId = queryParams.get("projectId");

    const getAccessTokenMutate = useMutation({
      mutationFn: ({ data }) => getAccessToken({ data }),
    });

    const {
      data: projectDetail,
      isFetching,
      isError,
      error,
    } = useQuery(["getProjectDetail", projectId], () => getProjectDetail(projectId), {
      enabled: !!projectId && !sns_token,
      refetchOnWindowFocus: false,
    });

    const { data: projectList, error: getProjectError } = useQuery(["getProjectList"], () => getProjectList(), {
      enabled: !!projectId && !sns_token,
      refetchOnWindowFocus: false,
    });

    const { data: projectRole, error: getRoleError } = useQuery(["getProjectRole", projectId], () => getProjectRole(projectId), {
      enabled: !!projectId && !sns_token && !checkingResource,
      refetchOnWindowFocus: false,
    });

    const { data: meData, isError: isErrorGetMe } = useQuery(["getMeInfo"], () => getMe(), {
      enabled: !!projectId && !sns_token && !checkingResource,
      refetchOnWindowFocus: false,
    });

    useEffect(() => {
      if (sns_token) {
        setAuthenticated(LOADING_STATUS.LOADING);
        const data = {
          sns_token: sns_token,
          grant_type: "sns",
          sns_redirect_url: REACT_APP_SITE_URL || document.location.origin,
        };
        getAccessTokenMutate.mutate(
          { data },
          {
            onSuccess: (response) => {
              const { access_token } = response?.data;
              localStorage.setItem(ACCESS_TOKEN, access_token);
              window.location.replace("?projectId=" + projectId);
            },
            onError: (error) => {
              console.error("Error fetching access token:", error);
              window.location.href = APP_ROUTES.ACCESS_DENIED;
            },
          }
        );
      }
      if (!sns_token && !projectId) {
        window.location.href = REACT_APP_PORTAL_URL;
      }
    }, []);

    useEffect(() => {
      if (projectDetail?.resource_types?.length) {
        const projectResources = [...new Set(projectDetail.resource_types.filter((item) => item !== null).map((item) => item.name))];
        setProjectResourcesState(projectResources);
        if (!projectResources.includes(resourceType)) {
          navigate(ROUTER_BY_RESOURCE[projectResources[0]]);
        }
      }
    }, [projectDetail]);

    useEffect(() => {
      if (projectRole?.name) {
        setProjectRoleState(projectRole.name);
        if (
          requiredRole &&
          projectRole.name !== requiredRole &&
          projectRole.name !== ROLES.ADMIN &&
          projectRole.name !== ROLES.SERVICE_ADMIN
        ) {
          window.location.href = APP_ROUTES.ACCESS_DENIED;
          return;
        }
        setCheckingRole(false);
      }
    }, [projectRole]);

    useEffect(() => {
      if (meData) {
        setAuthenticated(LOADING_STATUS.AUTHENTICATED);
        setAuth(meData);
      }
    }, [meData]);

    useEffect(() => {
      if (isErrorGetMe) {
        setAuthenticated(LOADING_STATUS.UNAUTHENTICATED);
        removeLocalStorage(ACCESS_TOKEN);
        setAuth({});
        window.location.href = REACT_APP_PORTAL_URL;
      }
    }, [isErrorGetMe]);

    useEffect(() => {
      if (projectList?.items) {
        setProjectListState(projectList.items);
        const projectIsValid = projectList.items.some((item) => item.id === projectId);
        if (!projectIsValid) {
          window.location.href = APP_ROUTES.ACCESS_DENIED;
          return;
        }
        setCheckingResource(false);
      }
    }, [projectList]);

    useEffect(() => {
      const statusCode = error?.response?.status;
      const getRoleStatusCode = getRoleError?.response?.status;
      const getProjectStatusCode = getProjectError?.response?.status;

      if (
        statusCode === STATUS_ERROR_CODE.NOT_FOUND ||
        getRoleStatusCode === STATUS_ERROR_CODE.NOT_FOUND ||
        getProjectStatusCode === STATUS_ERROR_CODE.NOT_FOUND
      ) {
        window.location.href = APP_ROUTES.ACCESS_DENIED;
      }
    }, [error, getRoleError, getProjectError]);

    if (authenticated !== LOADING_STATUS.AUTHENTICATED || isFetching || checkingResource || isError || checkingRole) {
      return (
        <div className="full-page d-flex align-items-center justify-content-center">
          <Spinner color="secondary" />
        </div>
      );
    }

    return <WrappedComponent {...wrappedProps} />;
  };

export default routeGuard;
